import React, { FC, CSSProperties } from 'react';
import { Button, ButtonProps, styled, Theme } from '@material-ui/core';

import { KeyboardShortcut } from 'components/KeyboardShortcut';
import { ShortcutAction } from 'constants/shortcuts';

const colors = {
  'grid-green': '#37AF59',
  'grid-light': '#C2E1F3',
  'grid-blue': '#3998CE',
} as const;

type StyledButtonProps = ButtonProps & {
  btnColor?: keyof typeof colors;
  hasIcon?: boolean;
};

const shortcutStyle: CSSProperties = {
  position: 'absolute',
  top: '1px',
  fontSize: '12px',
};

const StyledButton = styled(Button)<Theme, StyledButtonProps>(
  ({ theme, hasIcon, btnColor = 'green', variant = 'contained' }) => ({
    position: 'relative',
    height: '65px',
    padding: '0.5rem',
    color: 'rgba(0, 0, 0, 0.87); !important',
    borderStyle: 'none !important',
    fontWeight: 400,

    '& .MuiButton-label': {
      display: 'block',
      paddingTop: hasIcon && '1.5rem',
    },

    '& .shortcut': shortcutStyle,

    '& .icon': {
      position: 'absolute',
      bottom: '-5px',
      transform: 'translate(-50%, -50%)',
      left: '50%',
      '& svg': {
        stroke: theme.typography.button.color,
      },
    },

    ...{
      outlined: {
        border: `2px solid ${colors[btnColor]} !important`,
        '&:hover, &:focus, &:active': {
          border: `2px solid ${colors[btnColor]} !important`,
          filter: 'brightness(0.85) !important',
        },
        '&:disabled': {
          color: 'rgba(0, 0, 0, 0.45); !important',
          border: `2px solid ${colors[btnColor]} !important`,
          filter: 'grayscale(0.15) brightness(0.85) !important',
        },
      },
      contained: {
        background: colors[btnColor],
        '&:hover, &:focus, &:active': {
          background: `${colors[btnColor]} !important`,
          filter: 'brightness(0.85) !important',
        },
        '&:disabled': {
          color: 'rgba(0, 0, 0, 0.45); !important',
          background: `${colors[btnColor]} !important`,
          filter: 'grayscale(0.15) brightness(0.85) !important',
        },
      },
    }[variant],

    transition:
      'filter 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  }),
);

type Props = StyledButtonProps & {
  title: string;
  shortcut?: ShortcutAction;
  action: () => void;
  icon?: JSX.Element;
};

const ConfirmOptionButton: FC<Props> = ({
  title,
  variant = 'contained',
  shortcut,
  children,
  style,
  action,
  disabled,
  icon,
  ...props
}) => {
  return (
    <StyledButton
      hasIcon={!!icon}
      disableElevation
      fullWidth
      variant={variant}
      onClick={() => action && action()}
      style={{ ...style }}
      disabled={disabled}
      {...props}
    >
      <>
        {shortcut && (
          <KeyboardShortcut
            extraStyles={shortcutStyle}
            className="shortcut"
            shortcutAction={shortcut}
          />
        )}
        {icon && <p className="icon">{icon}</p>}
        {title}
        {children}
      </>
    </StyledButton>
  );
};

export default ConfirmOptionButton;
